// The Empower Tower (§33 S1, design "Empower Tower").
//
// A fuel source — Coal or Nuclear Generator, Sacred Well, Battery — with
// own Empower Towers in its network reach (`hubsOf`, cabling included) is a
// network. Every `EMPOWER.towersPerMove` towers grant one extra move a turn
// to an own piece within `EMPOWER.reach` (Manhattan, Q3) of the source that
// has not moved this turn: no pool spent, `EMPOWER.fodderFuel` or
// `powerFuel` points drawn from the network. Overlapping networks: the
// player names the source (`move.boost`).
//
// Factory Flow does not bind it (§33 T4), but a piece forged this turn is
// marked moved, so it never boosts one. Deterministic: ascending squares.

import { CLASS_FODDER, type Color, fileOf, rankOf } from "../board";
import { pieceClassAt, pieceIdAt, type Position } from "../position";
import { EMPOWER } from "../rules";
import { hubsOf, hubsReach, networkFuel } from "./power";
import {
  BATTERY,
  COAL_GENERATOR,
  EMPOWER_TOWER,
  type IndustryState,
  NUCLEAR_GENERATOR,
  structureAt,
  structureSquares,
  WELL
} from "./state";

const SOURCE_KINDS: readonly number[] = [COAL_GENERATOR, NUCLEAR_GENERATOR, WELL, BATTERY];

const manhattan = (a: number, b: number): number =>
  Math.abs(fileOf(a) - fileOf(b)) + Math.abs(rankOf(a) - rankOf(b));

/** Extra moves `owner`'s source at `at` grants a turn: towers in reach ÷ 2. */
export function boostGrants(industry: IndustryState, owner: Color, at: number): number {
  const source = structureAt(industry, at);
  if (!source || source.owner !== owner || !SOURCE_KINDS.includes(source.kind)) return 0;
  const hubs = hubsOf(industry, owner, at);
  const towers = structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return one.kind === EMPOWER_TOWER && one.owner === owner && hubsReach(hubs, sq);
  }).length;
  return Math.floor(towers / EMPOWER.towersPerMove);
}

/** Fuel points an extra move by the piece on `sq` costs. */
export const boostFuel = (position: Position, sq: number): number =>
  pieceClassAt(position, sq) === CLASS_FODDER ? EMPOWER.fodderFuel : EMPOWER.powerFuel;

/**
 * Why the piece on `from` may not take an extra move off the source at
 * `source` now, or null. The piece's side is `owner`; the move itself is
 * checked by the caller.
 */
export function boostRefusal(
  position: Position,
  industry: IndustryState,
  owner: Color,
  from: number,
  source: number
): string | null {
  const grants = boostGrants(industry, owner, source);
  if (grants === 0) return "That is no Empower network: it needs two Empower Towers in reach of your fuel source.";
  if ((structureAt(industry, source)!.boosts ?? 0) >= grants) return "That Empower network has granted its moves this turn.";
  if (manhattan(source, from) > EMPOWER.reach) return `That piece is more than ${EMPOWER.reach} squares from the network.`;
  const id = pieceIdAt(position, from);
  if (id > 0 && position.turnMoved.has(id)) return "That piece has already moved this turn.";
  if (networkFuel(industry, owner, source) < boostFuel(position, from)) {
    return `That Empower network needs ${boostFuel(position, from)} fuel points.`;
  }
  return null;
}

/** The sources whose network could grant the piece on `from` an extra move now, ascending. */
export function boostSources(position: Position, industry: IndustryState, owner: Color, from: number): number[] {
  return structureSquares(industry).filter(
    (sq) =>
      SOURCE_KINDS.includes(structureAt(industry, sq)!.kind) &&
      boostRefusal(position, industry, owner, from, sq) === null
  );
}

/** A network's count starts again with its owner's turn. */
export function refreshBoosts(industry: IndustryState, owner: Color): void {
  for (const sq of structureSquares(industry)) {
    const one = structureAt(industry, sq)!;
    if (one.owner === owner && one.boosts !== undefined) delete one.boosts;
  }
}
